import { IJBPPlanning, SMBFractionTypes } from 'partner360/entities/planning/model'

import { IPlanningSmb } from '../model/types'

/** Financial amounts/units only: percentages, quotas and completed-plan copies are not plans. */
const PLAN_FIELDS = [
  'SellThruSumUSDNet',
  'SellThruSumUSDNdp',
  'SellThruSumUSDTNet',
  'SellThruSumUSDUTNet',
  'SellThruSumLCNet',
  'SellThruSumLCNdp',
  'SellThruSumLCTNet',
  'SellThruSumLCUTNet',
  'SellThruUnits',
  'SellThruUnitsNdp',
  'SellThruUnitsTNet',
  'SellThruUnitsUTNet',
  'ContractualSumUSDNet',
  'ContractualSumUSDNdp',
  'ContractualSumUSDTNet',
  'ContractualSumUSDUTNet',
  'ContractualSumLCNet',
  'ContractualSumLCNdp',
  'ContractualSumLCTNet',
  'ContractualSumLCUTNet',
  'ContractualUnits',
  'ContractualUnitsNdp',
  'ContractualUnitsTNet',
  'ContractualUnitsUTNet',
] as const satisfies readonly (keyof IJBPPlanning)[]

interface IPlanningSmbYearScope {
  partnerId: string
  programType: string
  fYear: number
}

/** The horizon is Offering-wide, not the category that happened to open the editor. */
export const getPlanningSmbLastPlanYear = (
  planningData: IJBPPlanning[],
  {
    partnerId,
    programType,
    fYear,
    globalCategory,
  }: IPlanningSmbYearScope & {
    globalCategory?: string | null
  }
): number => {
  const futurePlans = planningData.filter(
    (row) =>
      row.PartnerId === partnerId &&
      row.ProgramTypes === programType &&
      row.FYear > fYear &&
      row.FYear <= fYear + 2 &&
      row.IsTarget &&
      !row.IsActuals &&
      !row.IsQuota &&
      !row.IsDirect &&
      !row.IsNoSplit &&
      !row.IsRollUp &&
      !row.IsPartnerCount &&
      (row.SMBFraction || SMBFractionTypes.Total) === SMBFractionTypes.Total &&
      (globalCategory == null || row.GlobalCategory === globalCategory)
  )

  for (const year of [fYear + 2, fYear + 1]) {
    const rows = futurePlans.filter((row) => row.FYear === year)
    const totals = rows.filter((row) => row.IsTotal)
    // Some older responses omit Total rows; all category plans still belong to this Offering.
    const source = totals.length ? totals : rows
    if (
      source.some((row) =>
        PLAN_FIELDS.some((field) => {
          const value = row[field]
          return typeof value === 'number' && Number.isFinite(value) && value !== 0
        })
      )
    )
      return year
  }

  return fYear
}

/** Keep the interval continuous: a plan in +2 includes +1 even when +1 is empty. */
export const getPlanningSmbYears = (fYear: number, lastPlanYear = fYear + 2): number[] =>
  Array.from(
    { length: Math.max(0, Math.min(2, lastPlanYear - fYear)) + 1 },
    (_, offset) => fYear + offset
  )

/** The apply procedure receives a full snapshot: hiding years must not delete old split data. */
export const preservePlanningSmbUnshownYears = (
  rows: IPlanningSmb[],
  planningSmb: IPlanningSmb[],
  {
    partnerId,
    programType,
    fYear,
    lastPlanYear = fYear + 2,
  }: IPlanningSmbYearScope & {
    lastPlanYear?: number
  }
): IPlanningSmb[] => [
  ...rows,
  ...planningSmb.filter(
    (row) =>
      row.PartnerID === partnerId &&
      row.ProgramType === programType &&
      row.FYear > lastPlanYear &&
      row.FYear <= fYear + 2
  ),
]
